// Fleet → Triage. Ports /media-screens, which was already the right idea:
// the fleet as a board of what needs a human, with verdicts computed
// server-side so the rules stay in one testable place. V2 keeps that API
// verbatim and only changes how it is framed — a lens, announced as one.

import React from 'react';
import { Link } from 'react-router-dom';
import { PageHead, Tiles, DataTable, Pill, Loading, ErrorState, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';

const TONE = { critical: 'bad', warn: 'warn', ok: 'grow' };

export default function Triage() {
  const { data, error, loading, reload } = useApi(
    () => api.get('/media-screens', { params: { limit: 500 } }).then((r) => r.data), []);

  if (loading) return <><Head /><Loading what="the fleet" /></>;
  if (error)   return <><Head /><ErrorState error={error} onRetry={reload} /></>;

  const d = data?.data || {};
  const p = d.pulse || {};
  const rows = d.rows || [];

  return (
    <>
      <Head onRefresh={reload} />
      <Tiles items={[
        { label: 'Critical',   value: p.critical ?? '—', tone: 'bad',  foot: 'need attention now' },
        { label: 'Warning',    value: p.warn ?? '—',     tone: 'warn' },
        { label: 'Online',     value: `${p.online ?? '—'} / ${p.total ?? '—'}`, tone: 'grow' },
        { label: 'Sensing',    value: p.sensing ?? '—',  foot: 'reporting perception' },
        { label: 'Plays 24h',  value: (p.plays24h ?? 0).toLocaleString() },
        { label: 'Rides 24h',  value: (p.rides24h ?? 0).toLocaleString(), foot: `${(p.km24h ?? 0).toLocaleString()} km` },
        { label: 'Agent lag',  value: p.agentLag?.behind ?? '—', tone: p.agentLag?.behind ? 'warn' : undefined,
          foot: `target ${p.agentLag?.target || p.agentTarget || '—'}` },
        { label: 'Unplaced',   value: p.unplaced ?? '—', foot: 'no placement set' },
      ]} />

      <DataTable
        rows={rows}
        getKey={(r) => r.thingName}
        filterOn={['plate', 'thingName', 'driverName', 'city', 'neighborhood', 'placementName', 'tenantName']}
        filterPlaceholder="Filter by plate, driver, city, thing name…"
        initialSort={{ key: 'status', dir: 'asc' }}
        columns={[
          { key: 'status', label: 'Verdict', width: 96,
            render: (r) => <Pill tone={TONE[r.status] || 'pend'}>{r.status || 'unknown'}</Pill>,
            sort: (r) => ({ critical: 0, warn: 1, ok: 2 }[r.status] ?? 3) },
          { key: 'plate', label: 'Screen', mono: true,
            render: (r) => (
              <Link to={`/v2/fleet/device/${encodeURIComponent(r.thingName)}`}
                    style={{ color: 'var(--iris-text)' }}>
                {r.plate || r.placementName || r.thingName}
              </Link>
            ) },
          // reasons are [{ severity, kind, text }] — the server ranks them, so
          // render in the order given and let severity carry the colour.
          { key: 'reasons', label: 'Why', wrap: true, sortable: false,
            render: (r) => (r.reasons?.length
              ? <span>{r.reasons.map((x, i) => {
                  const text = typeof x === 'string' ? x : x.text;
                  const sev = typeof x === 'string' ? null : x.severity;
                  return (
                    <span key={i} style={{
                      color: sev === 'critical' ? 'var(--bad-text)'
                           : sev === 'warn' ? 'var(--warn-text)' : 'var(--ink-soft)',
                    }}>
                      {i > 0 && <span style={{ color: 'var(--ink-faint)' }}> · </span>}
                      {text}
                    </span>
                  );
                })}</span>
              : <span style={{ color: 'var(--ink-faint)' }}>—</span>) },
          { key: 'driverName', label: 'Driver' },
          { key: 'city', label: 'City', render: (r) => r.neighborhood ? `${r.city} · ${r.neighborhood}` : r.city },
          { key: 'online', label: 'Link', width: 74,
            render: (r) => <Pill tone={r.online ? 'grow' : 'pend'}>{r.online ? 'online' : 'offline'}</Pill>,
            sort: (r) => (r.online ? 0 : 1) },
          { key: 'plays24h', label: 'Plays 24h', align: 'num', render: (r) => (r.plays24h ?? 0).toLocaleString() },
          { key: 'km24h', label: 'Km 24h', align: 'num', render: (r) => (r.km24h ?? 0).toLocaleString() },
          { key: 'agentVersion', label: 'Agent', mono: true },
        ]}
      />
    </>
  );
}

const Head = ({ onRefresh }) => (
  <PageHead
    title="Fleet triage"
    sub="The estate ranked by what needs a person, not by registration order. Verdicts are computed server-side so the rules are one place, testable, and identical wherever they are shown."
    actions={onRefresh ? <Button onClick={onRefresh}>Refresh</Button> : null}
  />
);
